import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import { Star } from 'lucide-react';



const ReportedReviewsTab = ({ toast, setLightboxImage }) => {
  const { hasPermission } = useAuth();

  // Data States
  const [reportedReviews, setReportedReviews] = useState([]);
  const [reportedReviewsLoading, setReportedReviewsLoading] = useState(true);

  const fetchReportedReviews = useCallback(async () => {
    if (!hasPermission('RESOLVE_ISSUES')) return;
    setReportedReviewsLoading(true);
    try {
      const res = await vendorService.getReportedReviewsAdmin();
      if (res.success && res.data) {
        setReportedReviews(res.data.reports || []);
      } else {
        toast.error(res.error || 'Failed to fetch reported reviews');
      }
    } catch (err) {
      toast.error('An error occurred while fetching reported reviews.');
      console.error(err);
    } finally {
      setReportedReviewsLoading(false);
    }
  }, [hasPermission, toast]);

  useEffect(() => {
    fetchReportedReviews();
  }, [fetchReportedReviews]);

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Are you sure you want to permanently delete this review? This action cannot be undone.")) {
      return;
    }
    try {
      const res = await vendorService.deleteReviewAdmin(reviewId);
      if (res.success) {
        toast.success("Review deleted successfully.");
        fetchReportedReviews();
      } else {
        toast.error(res.error || "Failed to delete review.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error deleting review.");
    }
  };

  const handleDismissReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to dismiss this report? The review will remain active.")) {
      return;
    }
    try {
      const res = await vendorService.dismissReportAdmin(reportId);
      if (res.success) {
        toast.success("Report dismissed successfully.");
        fetchReportedReviews();
      } else {
        toast.error(res.error || "Failed to dismiss report.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error dismissing report.");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 overflow-hidden text-left">
      <div className="mb-6 select-none">
        <h2 className="text-xl font-bold text-gray-800">Reported Reviews</h2>
        <p className="text-xs text-gray-400 mt-1 font-medium font-sans">
          Moderate reviews flagged by listing owners. Delete offending reviews or dismiss the reports.
        </p>
      </div>

      {reportedReviewsLoading ? (
        <div className="flex flex-col items-center justify-center py-20 select-none">
          <div className="w-9 h-9 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
          <span className="text-xs text-gray-400 mt-3 font-semibold">Fetching reported reviews...</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider bg-slate-50/50">
                <th className="py-3 px-4 pl-6">Review Details</th>
                <th className="py-3 px-4">Listing</th>
                <th className="py-3 px-4">Flagged By</th>
                <th className="py-3 px-4">Reason / Sub-reason</th>
                <th className="py-3 px-4">Report Date</th>
                <th className="py-3 px-4 pr-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reportedReviews.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-gray-400 font-bold text-sm bg-white">
                    No reported reviews found. Everything is clear!
                  </td>
                </tr>
              ) : (
                reportedReviews.map((rep) => {
                  const rev = rep.review || {};
                  return (
                    <tr key={rep.id} className="border-b border-gray-100 hover:bg-gray-50 text-xs text-gray-600 transition-colors last:border-0">
                      <td className="py-4 px-4 pl-6 max-w-xs text-left">
                        <div className="font-semibold text-gray-800 mb-0.5">{rev.user_name || "Anonymous"}</div>
                        <div className="text-[10px] text-gray-400 font-medium mb-1.5">{rev.user_email}</div>
                        <div className="flex gap-0.5 text-amber-400 mb-1.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              size={10}
                              fill={star <= rev.rating ? 'currentColor' : 'none'}
                              className={star <= rev.rating ? 'text-amber-400' : 'text-gray-200'}
                            />
                          ))}
                        </div>
                        <p className="text-[11px] leading-relaxed italic bg-gray-50/50 p-2 rounded-lg border border-gray-100 line-clamp-3">
                          "{rev.comment}"
                        </p>
                      </td>
                      <td className="py-4 px-4 font-semibold text-gray-800 max-w-[150px] truncate text-left">
                        {rev.company_name}
                      </td>
                      <td className="py-4 px-4 text-left">
                        <div className="font-medium text-gray-800">{rep.reporter_name || "Owner"}</div>
                        <div className="text-[10px] text-gray-400">{rep.reporter_email}</div>
                      </td>
                      <td className="py-4 px-4 text-left">
                        <span className="inline-flex px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100 font-bold text-[10px] uppercase tracking-wider mb-1">
                          {rep.reason}
                        </span>
                        {rep.sub_reason && (
                          <p className="text-[10px] text-gray-500 leading-relaxed max-w-[180px] line-clamp-2">
                            {rep.sub_reason}
                          </p>
                        )}
                      </td>
                      <td className="py-4 px-4 text-gray-500 font-medium whitespace-nowrap text-left">
                        {new Date(rep.created_at).toLocaleDateString('en-IN')}
                      </td>
                      <td className="py-4 px-4 pr-6 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleDismissReport(rep.id)}
                            className="px-2.5 py-1.5 border border-gray-200 text-gray-600 rounded-lg font-bold text-[10px] uppercase tracking-wider bg-white hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer shadow-sm"
                          >
                            Dismiss
                          </button>
                          <button
                            onClick={() => handleDeleteReview(rev.id)}
                            className="px-2.5 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-lg font-bold text-[10px] uppercase tracking-wider active:scale-[0.98] transition-all cursor-pointer shadow-sm shadow-rose-500/10"
                          >
                            Delete Review
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportedReviewsTab;
